import { Grid, GridCell } from '@almbrand/grid';
import { Headline } from '@almbrand/headline';
import { Modal } from '@almbrand/modal';
import { MultiColItem } from '@almbrand/multicolitem';
import { Paragraph } from '@almbrand/paragraph';
import { RichText } from '@almbrand/richtext';
import { SearchField } from '@almbrand/searchfield';
import { pushToDataLayer } from '@almbrand/trackinghooks';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import classNames from 'classnames';
import { formatMessage } from 'common/MessagesServicesMappers';
import { FormattedCustomerDocument } from 'common/MessagesServicesTypes';
import { DownloadTracking, InternalSearchTracking } from 'common/trackingUtils';
import { PreviewButton } from 'components/1-atoms';
import { DateTag } from 'components/1-atoms/DateTag';
import { LoadingSpinner } from 'components/1-atoms/LoadingSpinner';
import Dropdown from 'components/2-molecules/Dropdown/Dropdown';
import { PreviewModal } from 'components/2-molecules/PreviewModal/PreviewModal';
import { HttpStatusCode } from 'constantsValues';
import { ThemeNames } from 'constantsValues/themeNames';
import useDocumentPreview from 'hooks/useDocumentPreview';
import { useThemeName } from 'hooks/useThemeName';
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchDocuments } from 'store/slices/documentSlice';
import { getStatusMessage } from 'utilities/statusMessageUtils';
import styles from './MessageListComponent.module.scss';
import { FilterOptions, sortedDocs } from './MessageUtils';

export interface MessagesComponentProps {
	contentType?: string;
	contentId?: number;
	title?: string;

	statusMessages?: {
		items: Array<{
			content: {
				statusMessage: string;
				statusCode: HttpStatusCode;
			};
		}>;
	};
}

const MessagesListComponent: React.FC<MessagesComponentProps> = ({ contentType, contentId, title, statusMessages }) => {
	const dispatch = useAppDispatch();
	const docsToUse = useAppSelector((state) => state.documents.data);
	const status = useAppSelector((state) => state.documents.status);
	const { isMobile, isTablet } = useDeviceQuery();
	const trackingThemeName = useThemeName();
	const isCodan = trackingThemeName === ThemeNames.codan;

	const { openModal, previewDocument, handleMessageClick, handleMessageDownload, onModalClose, resetError, error } =
		useDocumentPreview();

	// Search state
	const [searchTerm, setSearchTerm] = useState('');
	// Dropdown state
	const [selectedOption, setSelectedOption] = useState<FilterOptions>();

	// Infinite scroll state
	const [visibleCount, setVisibleCount] = useState(30);
	const loaderRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (status === HttpStatusCode.Idle) {
			dispatch(fetchDocuments());
		}
	}, [status, dispatch]);

	// SearchTracking
	useEffect(() => {
		if (searchTerm === '') return; // Don't track empty searches

		const timeoutId = setTimeout(() => {
			pushToDataLayer(InternalSearchTracking(searchTerm, 'search results', trackingThemeName));
		}, 2000); // 2000ms delay

		return () => clearTimeout(timeoutId); // Cleanup timeout on searchTerm change
	}, [searchTerm, trackingThemeName]);

	// Format documents
	const formattedDocs = formatMessage(docsToUse) as FormattedCustomerDocument[];

	// Filtered documents based on search term
	const filteredDocs = formattedDocs.filter((item) => {
		const _searchField = searchTerm.trim().toLowerCase();
		return (
			item.name.toLowerCase().includes(_searchField) ||
			item.description?.toLowerCase().includes(_searchField) ||
			item.doc_type?.toLowerCase().includes(_searchField) ||
			item.date?.toLowerCase().includes(_searchField) ||
			item.policy_no?.toLowerCase().includes(_searchField) ||
			item.dateMinusDot?.toLowerCase().includes(_searchField)
		);
	});

	const handleOptionSelect = (option: FilterOptions) => {
		setSelectedOption(option);
		setVisibleCount(30); // ← reset scroll on new filter
	};

	// total number of docs after sorting (used for re-observing)
	const messagesTotalCount = sortedDocs(selectedOption, filteredDocs)?.length ?? 0;

	useEffect(() => {
		const node = loaderRef.current;
		if (!node) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setVisibleCount((count) => count + 30);
				}
			},
			{ rootMargin: '200px' }
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [visibleCount, messagesTotalCount]); // ← re-run whenever we’ve loaded more or filter changes

	const handleDocumentClick = (document: FormattedCustomerDocument) => {
		// Get file extension from MIME type
		const getFileExtension = (mimeType?: string) => mimeType?.split('/').pop() || '';
		const getMessageAction = isMobile || isTablet ? 'download' : 'preview';

		const downloadTracking = DownloadTracking(
			document.name,
			getFileExtension(document.content_type),
			getMessageAction,
			trackingThemeName
		);

		handleMessageClick(document.id, document.name, document.date, downloadTracking);
	};

	const shouldShowStatusMessage =
		status !== HttpStatusCode.Loading &&
		(status === HttpStatusCode.InternalServerError ||
			status === HttpStatusCode.NoContent ||
			messagesTotalCount == 0);

	return (
		<>
			<Grid
				className='pb-spacing-r'
				container
			>
				<GridCell
					desktopWidth='4col'
					tabletWidth='12col'
					mobileWidth='12col'
					nogutter
				>
					<Headline
						title={title}
						size='sm'
						variant='h1'
						fontColor='primary'
					/>

					<Paragraph
						text={`Her finder du de beskeder og dokumenter, vi har sendt til dig.`}
						fontColor='primaryDark'
						paragraphType={{
							type: 'standfirst',
							kind: 'title',
						}}
						className={styles.MessagesList__subHeading}
					/>
				</GridCell>

				<GridCell
					desktopWidth='7col'
					tabletWidth='12col'
					mobileWidth='12col'
					desktopOffset='1col'
					nogutter
					className={styles.MessagesList__filterContainer}
				>
					<Dropdown
						id={'dropdownFilter'}
						options={Object.values(FilterOptions).map((option, index) => ({
							id: index.toString(),
							title: option,
							value: option,
							resetOption: option === FilterOptions.ClearSorting,
						}))}
						placeholder='Sorter efter dato'
						onSelect={(item) => handleOptionSelect(item.value as FilterOptions)}
					/>

					<SearchField
						placeholder={'Søg efter dokument'}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
				</GridCell>
			</Grid>
			<Grid
				container
				className={styles.MessageList}
			>
				{status === HttpStatusCode.Loading && <LoadingSpinner />}
				{/* Render sorted documents */}

				{shouldShowStatusMessage && (
					<RichText
						className={classNames(styles.MessagesList__statusMessage, 'RichText')}
						description={getStatusMessage(
							statusMessages,
							status == HttpStatusCode.OK && messagesTotalCount < 1 ? HttpStatusCode.NoContent : status
						)}
					/>
				)}

				{!shouldShowStatusMessage &&
					sortedDocs(selectedOption, filteredDocs)
						.slice(0, visibleCount)
						.map((document) => (
							<MultiColItem
								key={document.id}
								className={styles.MessageList__item}
								title={document.name}
								subtitle={document.doc_type}
								paragraphSize={isCodan ? 'sm' : 'default'}
								{...(isCodan
									? {
											additionalInfo: [
												{
													text: document.formattedDate,
													paragraphType: {
														kind: 'paragraph',
														size: 'sm',
													},
													fontColor: 'primaryDark',
												},
											],
										}
									: {})}
								onClick={() => handleDocumentClick(document)}
								action={isCodan ? <PreviewButton /> : <DateTag date={document.formattedDate} />}
							/>
						))}

				{!shouldShowStatusMessage && messagesTotalCount > visibleCount && (
					<div
						ref={loaderRef}
						style={{ height: 1 }}
					/>
				)}

				{openModal && (
					<PreviewModal
						displayAsPdf={previewDocument.displayAsPdf}
						data={previewDocument.data}
						handleMessageDownload={handleMessageDownload}
						onClose={onModalClose}
					/>
				)}

				{error && (
					<Modal
						handleClose={() => resetError()}
						mode={'feedback'}
						mobileVariation={'dialog'}
					>
						<RichText description={getStatusMessage(statusMessages, HttpStatusCode.NotFound)} />
					</Modal>
				)}
			</Grid>
		</>
	);
};

export default MessagesListComponent;
